import { createSelector } from 'reselect';

import simpleSelectors from './simpleSelectors';
import * as module from './navSelectors';

/**
 * Ordered nav bar list: product categories, then direct categories, then an
 * "others" entry (only when navData.others is non-null).
 */
export const navItems = createSelector(
  [simpleSelectors.navData],
  (navData) => ([
    ...(navData.products || []).map((category) => ({
      type: 'product',
      slug: category.category_slug,
      title: category.title,
    })),
    ...(navData.directCategories || []).map((category) => ({
      type: 'direct',
      slug: category.category_slug,
      title: category.title,
    })),
    ...(navData.others ? [{
      type: 'others',
      slug: 'others',
      title: navData.others.title,
    }] : []),
  ]),
);

/**
 * Filters out hidden sub-categories and sorts sections/courses by `order`.
 * Shared by both product-card and direct-category structured views.
 */
export const structuredCourseView = (subCategories) => (subCategories || [])
  .filter((sub) => sub.is_displayed !== false)
  .slice()
  .sort((a, b) => a.order - b.order)
  .map((sub) => ({
    ...sub,
    courses: (sub.courses || []).slice().sort((a, b) => a.order - b.order),
  }));

const findProductCategory = (navData, slug) => (
  (navData.products || []).find((category) => category.category_slug === slug)
);

const findDirectCategory = (navData, slug) => (
  (navData.directCategories || []).find((category) => category.category_slug === slug)
);

/**
 * The products[] array for the currently-selected product-category nav item.
 */
export const selectedProductCategoryCards = createSelector(
  [simpleSelectors.navData, simpleSelectors.selectedNavItem],
  (navData, selectedNavItem) => {
    if (!selectedNavItem || selectedNavItem.type !== 'product') {
      return [];
    }
    const category = findProductCategory(navData, selectedNavItem.slug);
    return category ? (category.products || []) : [];
  },
);

/**
 * Resolves the current view to one of: productGrid, structured, flatList, empty.
 * Products are always shown as a grid/accordion (never drilled into as a
 * separate top-level view) — expanding one to see its courses is a purely
 * local UI concern handled inline by ProductCardGrid.
 */
export const selectedNavContent = createSelector(
  [
    simpleSelectors.navData,
    simpleSelectors.selectedNavItem,
    module.navItems,
  ],
  (navData, selectedNavItem, items) => {
    if (!selectedNavItem || items.length === 0) {
      return { kind: 'empty' };
    }
    if (selectedNavItem.type === 'product') {
      const category = findProductCategory(navData, selectedNavItem.slug);
      return { kind: 'productGrid', products: category ? (category.products || []) : [] };
    }
    if (selectedNavItem.type === 'direct') {
      const category = findDirectCategory(navData, selectedNavItem.slug);
      return {
        kind: 'structured',
        subCategories: module.structuredCourseView(category ? category.sub_categories : []),
      };
    }
    if (selectedNavItem.type === 'others') {
      return { kind: 'flatList', courses: (navData.others && navData.others.courses) || [] };
    }
    return { kind: 'empty' };
  },
);

/**
 * Flat list of every course_id present anywhere in navData (products, direct
 * categories, others), in response order.
 */
export const allCourseIds = createSelector(
  [simpleSelectors.navData],
  (navData) => {
    const ids = [];
    (navData.products || []).forEach((category) => {
      (category.products || []).forEach((product) => {
        (product.sub_categories || []).forEach((sub) => {
          (sub.courses || []).forEach((c) => ids.push(c.course_id));
        });
      });
    });
    (navData.directCategories || []).forEach((category) => {
      (category.sub_categories || []).forEach((sub) => {
        (sub.courses || []).forEach((c) => ids.push(c.course_id));
      });
    });
    if (navData.others) {
      (navData.others.courses || []).forEach((c) => ids.push(c.course_id));
    }
    return ids;
  },
);

/**
 * The new endpoint doesn't provide enrollment dates, so there's no longer a
 * true "most recently enrolled" course. This picks the first course_id found
 * anywhere in navData as a stand-in seed for recommendations.
 */
export const mostRecentCourseId = createSelector(
  [module.allCourseIds],
  (ids) => ids[0] || null,
);

export default {
  navItems,
  structuredCourseView,
  selectedProductCategoryCards,
  selectedNavContent,
  allCourseIds,
  mostRecentCourseId,
};
